import { TimelineSection } from '../../components/domain-sections/Timeline/TimelineSection';
import { EventsLogsSection } from '../../components';
import { generatePlanTimelineEvents } from '@shared/data/mockSubscriptionPlanDetails';
import { SAMPLE_PLAN_EVENTS } from '@shared/data/mockSubscriptionPlanEvents';
import { GeneralSection, PricingSection } from '../components';
import type { Plan } from '@/features/subscriptions/types/plans/plan.types';
import { usePanelSections, type SectionConfig } from '@shared/hooks/usePanelSections';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/components/ui/tabs';
import { SubscriptionsSection } from '../components/SubscriptionsSection';
import { AnalyticsSection } from '../components/AnalyticsSection';
import { mockPlanSubscriptions } from '@shared/data/mockPlanSubscriptions';

const PLAN_SECTIONS: SectionConfig[] = [
  {
    key: 'timeline',
    component: TimelineSection,
    props: {
      title: 'Activity',
      events: generatePlanTimelineEvents()
    }
  },
  {
    key: 'general',
    component: GeneralSection
  },
  {
    key: 'pricing',
    component: PricingSection
  },
  {
    key: 'eventsLogs',
    component: EventsLogsSection,
    props: {
      eventsData: SAMPLE_PLAN_EVENTS
    }
  }
];

interface PlanSectionsProps {
  isDetailView?: boolean;
  planData?: Plan;
}

export function PlanSections({ isDetailView = false, planData }: PlanSectionsProps) {
  const sectionsWithDynamicProps = PLAN_SECTIONS.map(section => {
    // Timeline section needs events generated from planData
    if (section.key === 'timeline' && planData) {
      return {
        ...section,
        props: {
          ...section.props,
          events: generatePlanTimelineEvents(planData) // Pass planData to generate real events
        }
      };
    }

    // All other sections receive planData directly
    if (planData) {
      return {
        ...section,
        props: {
          ...section.props,
          planData
        }
      };
    }

    return section;
  });

  const { renderSections } = usePanelSections(sectionsWithDynamicProps);

  // Filter subscriptions by planId if planData is available
  const planSubscriptions = planData
    ? mockPlanSubscriptions.filter(sub => sub.planId === planData.id)
    : mockPlanSubscriptions;

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList>
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
        <TabsTrigger value="analytics">Analytics</TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        {renderSections(isDetailView)}
      </TabsContent>

      <TabsContent value="subscriptions">
        <SubscriptionsSection subscriptions={planSubscriptions} isDetailView={isDetailView} showFilters={true} />
      </TabsContent>

      <TabsContent value="analytics">
        <AnalyticsSection isDetailView={isDetailView} />
      </TabsContent>
    </Tabs>
  );
}
